import type { ReactNode } from 'react';
import type { ActionContext, TableAction, TableApi } from '../../domain';
import type { TableAdapter } from '../../ports';

export interface ResolvedAction<T> {
    action: TableAction<T>;
    label: string;
    disabled: boolean;
    tooltip?: string;
    run: () => void;
}

export interface ActionView {
    id: string;
    icon?: ReactNode;
    variant?: 'primary' | 'secondary';
}

export interface ToolbarItem extends Omit<ResolvedAction<never>, 'action'> {
    action: ActionView;
}

interface ChildActions {
    actions?: TableAction<unknown>[];
    selectedRows: unknown[];
    adapter: TableAdapter<unknown>;
    api: TableApi<unknown>;
}

interface UseActionsArgs<T> {
    actions?: TableAction<T>[] | ((ctx: ActionContext<T>) => TableAction<T>[]);
    child?: ChildActions;
    expanded: boolean;
    selectedRows: T[];
    adapter: TableAdapter<T>;
    api: TableApi<T>;
}

interface UseActionsResult<T> {
    toolbar: ToolbarItem[];
    rowMenu: (row: T) => ResolvedAction<T>[];
    hasRowActions: boolean;
}

function resolveActionList<R>(
    actions:
        | TableAction<R>[]
        | ((ctx: ActionContext<R>) => TableAction<R>[])
        | undefined,
    ctx: ActionContext<R>,
): TableAction<R>[] {
    if (!actions) return [];
    return typeof actions === 'function' ? actions(ctx) : actions;
}

function resolveLabel<R>(
    action: TableAction<R>,
    ctx: ActionContext<R>,
): string | null {
    const label =
        typeof action.label === 'function' ? action.label(ctx) : action.label;
    return label || null;
}

function toResolvedAction<R>(
    action: TableAction<R>,
    ctx: ActionContext<R>,
    label: string,
): ResolvedAction<R> {
    const outcome = action.disabled?.(ctx);
    const disabled =
        outcome === true || (typeof outcome === 'object' && outcome.disabled);
    const tooltip = typeof outcome === 'object' ? outcome.tooltip : undefined;
    return {
        action,
        label,
        disabled,
        tooltip,
        run: () => {
            action.onClick(ctx);
        },
    };
}

function buildToolbar<R>(
    actions: TableAction<R>[],
    ctx: ActionContext<R>,
): ResolvedAction<R>[] {
    const result: ResolvedAction<R>[] = [];
    for (const action of actions) {
        if (action.row || action.hidden?.(ctx)) continue;
        const label = resolveLabel(action, ctx);
        if (!label) continue;
        const resolved = toResolvedAction(action, ctx, label);
        if (!resolved.disabled) result.push(resolved);
    }
    return result;
}

function buildRowMenu<R>(
    actions: TableAction<R>[],
    ctx: ActionContext<R>,
): ResolvedAction<R>[] {
    const result: ResolvedAction<R>[] = [];
    for (const action of actions) {
        if (!action.row || action.hidden?.(ctx)) continue;
        const label = resolveLabel(action, ctx);
        if (!label) continue;
        result.push(toResolvedAction(action, ctx, label));
    }
    return result;
}

export function useActions<T>(args: UseActionsArgs<T>): UseActionsResult<T> {
    const parentCtx: ActionContext<T> = {
        selectedRows: args.selectedRows,
        adapter: args.adapter,
        api: args.api,
    };
    const parentActions = resolveActionList(args.actions, parentCtx);

    const child =
        args.selectedRows.length === 0 && args.expanded
            ? args.child
            : undefined;

    const toolbar: ToolbarItem[] = child
        ? buildToolbar(child.actions ?? [], {
              selectedRows: child.selectedRows,
              adapter: child.adapter,
              api: child.api,
              parentAdapter: args.adapter as unknown as TableAdapter<unknown>,
          })
        : buildToolbar(parentActions, parentCtx);

    const rowMenu = (row: T): ResolvedAction<T>[] => {
        const ctx: ActionContext<T> = { ...parentCtx, row };
        return buildRowMenu(resolveActionList(args.actions, ctx), ctx);
    };

    const hasRowActions = parentActions.some(
        (action) => action.row && !action.hidden?.(parentCtx),
    );

    return { toolbar, rowMenu, hasRowActions };
}
